import {
   BasicFacetWithTermsV2,
   ElasticFacetsV2,
   FacetContentV2Response,
   FacetKeysV2,
   facetLabelTranslationMappingV2,
   FacetV2Response,
   FilterRange,
   technicalProperties
} from '@ravago/shared/page-data/models/elastic';
import { TranslateFn } from '@ravago/shared/translations/client';

import { notchedImpactConfig } from '../../../shared/physical-property-display/mappers/property-unit-labels.mapper';
import { FacetContentV2, FacetGroupStructureV2 } from '../models/facet-group.model';

import { queryToFacetsV2 } from './filter-query-utils';

export const getFacetsFromParams = (searchParams: { [key: string]: string | string[] | undefined }): BasicFacetWithTermsV2[] =>
   queryToFacetsV2(searchParams);

const extractTechnicalRange = (facet: BasicFacetWithTermsV2, consumer: (range: FilterRange) => void) => {
   if (facet && facet.terms && facet.terms[0].range) {
      consumer({
         min: facet.terms[0].range.min,
         max: facet.terms[0].range.max
      });
   }
};

function roundTo(num: number, precision: number, to: 'up' | 'down') {
   const factor = 10 ** precision;
   return to === 'up' ? Math.ceil(num * factor) / factor : Math.floor(num * factor) / factor;
}

export const orderTerms = (terms: FacetContentV2Response[], parentKey: string): FacetContentV2Response[] => {
   const flameRatingOrder = ['HB', 'V-2', 'V-1', 'V-0', '5VB', '5VA', '5V'];

   switch (parentKey) {
      case 'flame-rating':
         return terms.sort(
            (termA, termB) =>
               flameRatingOrder.findIndex((termKey) => termKey === termA.key) - flameRatingOrder.findIndex((termKey) => termKey === termB.key)
         );
      default:
         return terms;
   }
};

export function mapResponseToFacetGroups(
   responseFacets: FacetV2Response,
   translate: TranslateFn,
   isNestedProperty?: boolean,
   allowTechnicalPropertyFilters?: boolean
): FacetGroupStructureV2[] {
   type MappedFacetContent = FacetContentV2Response & { hideCount?: boolean };

   const FLAME_RATING_KEY = 'flame-rating';
   const FLAME_RATING_COLLECTION_KEY = 'flame-rating-collection';
   const stepMapper: { [key: string]: number } = { density: 0.01 };
   const keepOriginalOrderFacets: string[] = [FLAME_RATING_COLLECTION_KEY];
   const disableSlider: string[] = ['flow-rate', 'flame-rating', 'notched-impact', 'notched-impact-strength'];
   const canHaveFillInBoxes: string[] = [...technicalProperties];

   const combinedFacetGroupStructure: FacetGroupStructureV2[] = [];
   const createFacetGroupStructureFromResponse = (responseFacet: MappedFacetContent): FacetContentV2 => ({
      label: responseFacet.label,
      range: responseFacet.range && responseFacet.range.min !== responseFacet.range.max ? responseFacet.range : undefined,
      count: responseFacet.count,
      hideCount: responseFacet.hideCount,
      key: responseFacet.key,
      rangeDisplay: {
         hasFillInBoxes: canHaveFillInBoxes.includes(responseFacet.key) && !!responseFacet.range,
         step: stepMapper[responseFacet.key],
         useSlider: !disableSlider.includes(responseFacet.key)
      },
      unit: responseFacet.unit,
      nestedFacetGroups: responseFacet.nested
         ? mapResponseToFacetGroups(responseFacet.nested, translate, true, allowTechnicalPropertyFilters)
         : undefined,
      keepFacetOrder: keepOriginalOrderFacets.includes(responseFacet.key)
   });

   (Object.keys(responseFacets) as (keyof ElasticFacetsV2)[]).forEach((facetKey) => {
      const facetContent = responseFacets[facetKey]!;

      if (technicalProperties.includes(facetKey as any) && !isNestedProperty) {
         if (!allowTechnicalPropertyFilters) return;
         const translationKey = facetLabelTranslationMappingV2.get(facetKey) ?? facetKey;
         const placeholderConfig = facetTranslationPlaceholders[facetKey as FacetKeysV2];
         let technicalPropertySubFacet: MappedFacetContent;
         const flameRatingCandidate =
            facetKey === FLAME_RATING_KEY && facetContent.length === 1 && facetContent[0].key === FLAME_RATING_KEY;

         if (flameRatingCandidate) {
            const [flameRatingParent] = facetContent;
            const nestedFlameRatings = flameRatingParent.nested?.[FLAME_RATING_KEY];
            const label = translate(translationKey, placeholderConfig?.placeholders);

            if (nestedFlameRatings && nestedFlameRatings.length > 0) {
               technicalPropertySubFacet = {
                  key: FLAME_RATING_COLLECTION_KEY,
                  label,
                  count: flameRatingParent.count,
                  nested: { [FLAME_RATING_KEY]: orderTerms(nestedFlameRatings, FLAME_RATING_KEY) }
               };
            } else {
               // Fallback for backwards compatibility
               technicalPropertySubFacet = {
                  key: FLAME_RATING_KEY,
                  label,
                  count: 0,
                  hideCount: true
               };
            }
         } else if (facetContent.length > 1) {
            const combinedCount = facetContent.reduce((acc, curr) => acc + curr.count, 0);
            const label = translate(translationKey, placeholderConfig?.placeholders);
            technicalPropertySubFacet = {
               key: `${facetKey}-collection`,
               label,
               count: combinedCount === 0 ? 0 : -1,
               nested: { [facetKey]: orderTerms(facetContent, facetKey) }
            };
         } else if (facetContent.length === 0) {
            const label = translate(translationKey, placeholderConfig?.placeholders);
            technicalPropertySubFacet = {
               key: facetKey,
               label,
               count: 0
            };
         } else {
            const [techProp, unit] = facetContent[0].key.split(':');
            const label = translate(translationKey, placeholderConfig?.withUnit?.(unit as 'SI' | 'US') ?? placeholderConfig?.placeholders);
            technicalPropertySubFacet = {
               key: techProp,
               label,
               range: facetContent[0].range
                  ? {
                       min: facetContent[0].range.min !== undefined ? roundTo(facetContent[0].range.min, 2, 'down') : undefined,
                       max: facetContent[0].range.max !== undefined ? roundTo(facetContent[0].range.max, 2, 'up') : undefined
                    }
                  : undefined,
               count: facetContent[0].count,
               unit: unit as 'SI' | 'US'
            };
         }
         const technicalPropertiesFacetGroup = combinedFacetGroupStructure.find((facetGroup) => facetGroup.identifier.key === 'technical-properties');
         if (technicalPropertiesFacetGroup) {
            technicalPropertiesFacetGroup.facets.push(createFacetGroupStructureFromResponse(technicalPropertySubFacet));
         } else {
            combinedFacetGroupStructure.push({
               identifier: { key: 'technical-properties', label: 'technical-properties', collectionGroup: true },
               facets: [createFacetGroupStructureFromResponse(technicalPropertySubFacet)]
            });
         }
      } else {
         combinedFacetGroupStructure.push({
            identifier: { key: facetKey, label: facetKey },
            facets: facetContent.map((facet) => createFacetGroupStructureFromResponse(facet))
         });
      }
   });

   return combinedFacetGroupStructure;
}

export const facetTransformers: {
   [K in FacetKeysV2]: (acc: ElasticFacetsV2, facet: Extract<BasicFacetWithTermsV2, { facetName: string }>) => void;
} = {
   country: (acc, f) => {
      acc.country = f.terms.map((t) => t.key);
   },
   brand: (acc, f) => {
      acc.brand = f.terms.map((t) => t.key);
   },
   producer: (acc, f) => {
      acc.producer = f.terms.map((t) => t.key);
   },
   group: (acc, f) => {
      acc.group = f.terms.map((t) => t.key);
   },
   subgroup: (acc, f) => {
      acc.subgroup = f.terms.map((t) => t.key);
   },
   quality: (acc, f) => {
      acc.quality = f.terms.map((t) => t.key);
   },
   feature: (acc, f) => {
      acc.feature = f.terms.map((t) => t.key);
   },
   filler: (acc, f) => {
      acc.filler = f.terms.map((t) => ({ key: t.key, range: t.range }));
   },
   industry: (acc, f) => {
      acc.industry = f.terms.map((t) => t.key);
   },
   'processing-method': (acc, f) => {
      acc['processing-method'] = f.terms.map((t) => t.key);
   },
   segment: (acc, f) => {
      acc.segment = f.terms.map((t) => t.key);
   },
   'sustainability-group': (acc, f) => {
      acc['sustainability-group'] = f.terms.map((t) => t.key);
   },
   'sub-sustainability': (acc, f) => {
      acc['sub-sustainability'] = f.terms.map((t) => t.key);
   },
   visibility: (acc, f) => {
      acc.visibility = f.terms.map((t) => t.key);
   },
   'flame-rating': (acc, f) => {
      acc['flame-rating'] = f.terms.map((t) => t.key);
   },
   density: (acc, f) => {
      extractTechnicalRange(f, (val) => {
         acc.density = val;
      });
   },
   'flow-rate': (acc, f) => {
      extractTechnicalRange(f, (val) => {
         acc['flow-rate'] = val;
      });
   },
   'notched-impact': (acc, f) => {
      extractTechnicalRange(f, (val) => {
         acc['notched-impact'] = val;
      });
   },
   'notched-impact-strength': (acc, f) => {
      extractTechnicalRange(f, (val) => {
         acc['notched-impact-strength'] = val;
      });
   }
};

export const propertyUnitSuffix: { [techProp: string]: { SI: string; US: string } } = {
   density: {
      SI: 'g/cm³',
      US: 'g/cm³'
   },
   'flow-rate': {
      SI: 'g/10 min',
      US: 'g/10 min'
   },
   'notched-impact': {
      SI: 'J/m',
      US: 'ft-lb/in'
   },
   'notched-impact-strength': {
      SI: 'kJ/m²',
      US: 'ft-lb/in²'
   }
};

export type FacetPlaceholderConfig = {
   /** Placeholders used when the unit system is not yet known (e.g. collection or empty state). */
   placeholders: Record<string, string>;
   /** Optional override that also injects the resolved unit suffix once the unit system is known. */
   withUnit?: (unit: 'SI' | 'US') => Record<string, string>;
};

/**
 * Extend this map to add translation placeholders for any facet key.
 * The mapper picks them up automatically — no changes needed there.
 */
export const facetTranslationPlaceholders: Partial<Record<FacetKeysV2, FacetPlaceholderConfig>> = {
   'notched-impact': {
      placeholders: notchedImpactConfig.ASTM.SI,
      withUnit: (unit) => notchedImpactConfig.ASTM[unit]
   },
   'notched-impact-strength': {
      placeholders: notchedImpactConfig.ISO.SI,
      withUnit: (unit) => notchedImpactConfig.ISO[unit]
   }
};

/**
 * Returns the resolved translation placeholders for a given facet name and optional unit system.
 */
export const getFacetTranslationPlaceholders = (facetName: string, unitSystem?: 'SI' | 'US'): Record<string, string> | undefined => {
   const config = (facetTranslationPlaceholders as Record<string, FacetPlaceholderConfig | undefined>)[facetName];
   if (!config) return undefined;
   return unitSystem ? config.withUnit?.(unitSystem) ?? config.placeholders : config.placeholders;
};

export const mapFacetsToObject = (facets: BasicFacetWithTermsV2[]): ElasticFacetsV2 => {
   const result: ElasticFacetsV2 = {};
   for (let i = 0; i < facets.length; i += 1) {
      const facet = facets[i];
      if (facet.facetName in facetTransformers) {
         facetTransformers[facet.facetName as FacetKeysV2](result, facet as BasicFacetWithTermsV2);
      }
   }
   return result;
};
